import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50" data-testid="site-header">
      <div className="backdrop-blur-xl bg-white/70 border-b border-ink/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex flex-col leading-none group" data-testid="nav-logo">
            <span className="text-2xl font-bold tracking-tight text-[#1B3A6B] group-hover:text-cobalt transition-colors duration-300">∆XIOM</span>
            <span className="mt-1 text-[10px] uppercase tracking-[0.35em] text-ink/50">
              Computers
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8" data-testid="nav-desktop">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-sm tracking-wide transition-colors duration-300 ${
                    isActive ? "text-cobalt" : "text-ink/70 hover:text-ink"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute -bottom-1.5 left-0 h-px bg-cobalt transition-[width] duration-300 ${
                        isActive ? "w-full" : "w-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-ink text-paper text-sm px-5 py-2.5 transition-transform duration-200 hover:scale-[0.98] hover:bg-cobalt"
            >
              Start a project
              <ArrowUpRight size={15} />
            </Link>
            <button
              className="md:hidden text-ink p-2"
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden backdrop-blur-xl bg-white/90 border-b border-ink/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-display text-2xl ${isActive ? "text-cobalt" : "text-ink"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
